import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import LogoUploader from "@/components/LogoUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Trash2, GraduationCap, BookOpen, Users, CalendarRange, AlertTriangle } from "lucide-react";

function EntityForm({ fields, onSubmit, busy, testIdPrefix }) {
  const [form, setForm] = useState(() =>
    Object.fromEntries(fields.map((f) => [f.key, f.type === "number" ? "" : ""]))
  );

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    fields.forEach((f) => {
      if (f.type === "number") payload[f.key] = parseInt(form[f.key], 10) || 0;
    });
    onSubmit(payload, () => setForm(Object.fromEntries(fields.map((f) => [f.key, ""]))));
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-6 mb-6 grid sm:grid-cols-2 gap-4" data-testid={`${testIdPrefix}-form`}>
      {fields.map((f) => (
        <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
          <Label className="font-heading">{f.label}</Label>
          <Input
            type={f.type || "text"} required={!f.optional}
            value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
            placeholder={f.placeholder}
            className="rounded-2xl h-12 mt-1.5 border-brand-purple/20"
            data-testid={`${testIdPrefix}-${f.key}-input`}
          />
        </div>
      ))}
      <div className="sm:col-span-2">
        <Button type="submit" disabled={busy}
          className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-7"
          data-testid={`${testIdPrefix}-submit-btn`}>
          {busy ? "Saving..." : "Add"}
        </Button>
      </div>
    </form>
  );
}

export default function BranchDetailPage() {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [branch, setBranch] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [activeYear, setActiveYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const br = await api.get(`/branches/${branchId}`);
      setBranch(br.data);
      const [t, s, c, y] = await Promise.all([
        api.get("/teachers", { params: { branch_id: branchId } }),
        api.get("/students", { params: { branch_id: branchId } }),
        api.get("/classes", { params: { branch_id: branchId } }),
        api.get("/school-years/active", { params: { branch_id: branchId } }),
      ]);
      setTeachers(t.data || []);
      setStudents(s.data || []);
      setClasses(c.data || []);
      setActiveYear(y.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchId]);

  const addTeacher = async (data, reset) => {
    setBusy(true);
    try {
      const res = await api.post("/teachers", { branch_id: branchId, ...data });
      setTeachers([res.data, ...teachers]);
      reset(); toast.success("Teacher added");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const addStudent = async (data, reset) => {
    setBusy(true);
    try {
      const res = await api.post("/students", { branch_id: branchId, ...data });
      setStudents([res.data, ...students]);
      reset(); toast.success("Student added");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const addClass = async (data, reset) => {
    setBusy(true);
    try {
      const res = await api.post("/classes", { branch_id: branchId, ...data });
      setClasses([res.data, ...classes]);
      reset(); toast.success("Class added");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (kind, id) => {
    try {
      await api.delete(`/${kind}/${id}`);
      if (kind === "teachers") setTeachers(teachers.filter((x) => x.id !== id));
      if (kind === "students") setStudents(students.filter((x) => x.id !== id));
      if (kind === "classes") setClasses(classes.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) {
    return (
      <>
        <div className="text-brand-inkMuted font-heading">Loading branch...</div>
      </>
    );
  }

  if (!branch) return null;

  return (
    <>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-brand-purple font-semibold mb-6 hover:underline" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-7 mb-6">
        <div className="flex flex-wrap items-start gap-5 justify-between">
          <div className="flex items-start gap-4 min-w-0">
            <LogoUploader
              kind="branches"
              id={branch.id}
              logo={branch.logo_base64}
              size="lg"
              editable={user?.role === "school_owner" && branch.status === "approved"}
              onUpdated={(newLogo) => setBranch((b) => (b ? { ...b, logo_base64: newLogo } : b))}
            />
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground break-all">{branch.name}</h1>
                <StatusBadge status={branch.status} />
              </div>
              <p className="text-brand-inkMuted mt-1">Part of <span className="font-semibold text-foreground">{branch.school_name}</span></p>
              <p className="text-sm text-brand-inkMuted mt-2">{branch.address}</p>
            </div>
          </div>
          {activeYear && (
            <div
              className="inline-flex items-center gap-2 rounded-full bg-brand-purpleLight px-4 py-1.5 text-sm font-semibold text-brand-purpleHover"
              data-testid="active-year-pill"
            >
              <CalendarRange className="w-4 h-4" />
              School Year: {activeYear.label}
            </div>
          )}
        </div>
      </div>

      {!activeYear && (
        <div
          className="bg-[#FFF3E0] border border-[#FFB74D]/50 rounded-3xl p-5 mb-6 flex flex-wrap items-center justify-between gap-3"
          data-testid="no-active-year-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-[#E65100]" />
            </div>
            <div>
              <p className="font-heading font-semibold text-foreground">No active school year</p>
              <p className="text-sm text-brand-inkMuted">
                Activate a school year in Settings before enrolling students or creating classes & events.
              </p>
            </div>
          </div>
          <Link to="/settings">
            <Button
              className="rounded-full bg-brand-orange hover:bg-brand-orangeHover text-foreground shadow-sm"
              data-testid="go-to-settings-btn"
            >
              Go to Settings
            </Button>
          </Link>
        </div>
      )}

      <Tabs defaultValue="teachers" className="w-full">
        <TabsList className="bg-brand-purpleLight rounded-full p-1 h-auto">
          <TabsTrigger value="teachers" className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-brand-purpleHover" data-testid="tab-teachers">
            <GraduationCap className="w-4 h-4 mr-1" /> Teachers ({teachers.length})
          </TabsTrigger>
          <TabsTrigger value="classes" className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-brand-purpleHover" data-testid="tab-classes">
            <BookOpen className="w-4 h-4 mr-1" /> Classes ({classes.length})
          </TabsTrigger>
          <TabsTrigger value="students" className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-brand-purpleHover" data-testid="tab-students">
            <Users className="w-4 h-4 mr-1" /> Students ({students.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="teachers" className="mt-6">
          <EntityForm
            testIdPrefix="teacher"
            fields={[
              { key: "name", label: "Full name", placeholder: "Jane Doe" },
              { key: "email", label: "Email", type: "email", placeholder: "jane@school.com" },
              { key: "subject", label: "Subject", placeholder: "Mathematics" },
              { key: "phone", label: "Phone", optional: true, placeholder: "+1 555 0300" },
            ]}
            onSubmit={addTeacher} busy={busy}
          />
          <ListGrid items={teachers} kind="teachers" onRemove={remove}
            render={(t) => (
              <>
                <h3 className="font-heading text-lg font-semibold text-foreground">{t.name}</h3>
                <p className="text-sm text-brand-inkMuted">{t.subject}</p>
                <p className="text-sm text-brand-inkMuted mt-1">{t.email}</p>
                {t.phone && <p className="text-sm text-brand-inkMuted">{t.phone}</p>}
              </>
            )}
            icon={GraduationCap}
          />
        </TabsContent>

        <TabsContent value="classes" className="mt-6">
          <EntityForm
            testIdPrefix="class"
            fields={[
              { key: "name", label: "Class name", placeholder: "Sunbeams" },
              { key: "grade", label: "Grade", placeholder: "Grade 1" },
            ]}
            onSubmit={addClass} busy={busy}
          />
          <ListGrid items={classes} kind="classes" onRemove={remove}
            render={(c) => (
              <>
                <h3 className="font-heading text-lg font-semibold text-foreground">{c.name}</h3>
                <p className="text-sm text-brand-inkMuted">{c.grade}</p>
              </>
            )}
            icon={BookOpen}
          />
        </TabsContent>

        <TabsContent value="students" className="mt-6">
          <EntityForm
            testIdPrefix="student"
            fields={[
              { key: "name", label: "Student name", placeholder: "Tommy Smith" },
              { key: "age", label: "Age", type: "number", placeholder: "6" },
              { key: "parent_name", label: "Parent name", placeholder: "Anna Smith" },
              { key: "parent_phone", label: "Parent phone", placeholder: "+1 555 0400" },
            ]}
            onSubmit={addStudent} busy={busy}
          />
          <ListGrid items={students} kind="students" onRemove={remove}
            render={(s) => (
              <>
                <h3 className="font-heading text-lg font-semibold text-foreground">{s.name}</h3>
                <p className="text-sm text-brand-inkMuted">Age {s.age}</p>
                <p className="text-sm text-brand-inkMuted mt-1">Parent: {s.parent_name}</p>
                <p className="text-sm text-brand-inkMuted">{s.parent_phone}</p>
              </>
            )}
            icon={Users}
          />
        </TabsContent>
      </Tabs>
    </>
  );
}

function ListGrid({ items, kind, onRemove, render, icon: Icon }) {
  if (items.length === 0) {
    return (
      <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center text-brand-inkMuted" data-testid={`no-${kind}`}>
        <div className="w-12 h-12 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
          <Icon className="w-6 h-6 text-brand-purple" />
        </div>
        <p>No {kind} yet. Add your first one above.</p>
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((it) => (
        <div key={it.id} className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-5 hover:-translate-y-1 hover:shadow-md transition-all duration-300" data-testid={`${kind}-card-${it.id}`}>
          <div className="flex items-start justify-between mb-2">
            <div className="w-10 h-10 rounded-2xl bg-brand-purpleLight flex items-center justify-center">
              <Icon className="w-5 h-5 text-brand-purple" />
            </div>
            <button onClick={() => onRemove(kind, it.id)} className="text-brand-inkMuted hover:text-[#C62828]" data-testid={`delete-${kind}-${it.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          {render(it)}
        </div>
      ))}
    </div>
  );
}
